import React, { memo, useMemo, useEffect, useState } from 'react';
import { Result, Alert, Typography } from 'antd';
import { MobileOutlined } from '@ant-design/icons';
import { useApp } from '../context/AppContext';
import './TelegramLoader.css';

const { Paragraph } = Typography;

const TelegramLoader = memo(({ isLoading, error }) => {
  const { t } = useApp();
  const [loadingText, setLoadingText] = useState('');
  
  useEffect(() => {
    setLoadingText(t('loader_init'));
  }, [t]);

  // Анимация текста загрузки
  useEffect(() => {
    if (!isLoading) return;
    
    const texts = [
      t('loader_init'),
      t('loader_connecting'),
      t('loader_setup'),
      t('loader_verify'),
      t('loader_almost'),
    ];
    
    let currentIndex = 0;
    const interval = setInterval(() => {
      currentIndex = (currentIndex + 1) % texts.length;
      setLoadingText(texts[currentIndex]);
    }, 800);
    
    return () => clearInterval(interval);
  }, [isLoading, t]);

  const loadingContainerStyles = useMemo(() => ({
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    flexDirection: 'column',
    gap: '24px',
    background: 'var(--tg-theme-bg-color, #ffffff)',
    color: 'var(--tg-theme-text-color, #000000)',
    position: 'relative',
    overflow: 'hidden'
  }), []);

  const titleStyles = useMemo(() => ({
    fontSize: '20px',
    fontWeight: '600',
    color: 'var(--tg-theme-text-color, #000000)',
    textAlign: 'center',
    margin: 0,
    opacity: isLoading ? 1 : 0,
    transition: 'opacity 0.3s ease'
  }), [isLoading]);

  const subtitleStyles = useMemo(() => ({
    fontSize: '14px',
    color: 'var(--tg-theme-hint-color, #999999)',
    textAlign: 'center',
    margin: 0,
    opacity: isLoading ? 0.8 : 0,
    transition: 'opacity 0.3s ease'
  }), [isLoading]);

  if (isLoading) {
    return (
      <div style={loadingContainerStyles}>
        {/* Фоновые анимированные элементы */}
        <div className="telegram-bg-animation">
          <div className="floating-circle circle-1"></div>
          <div className="floating-circle circle-2"></div>
          <div className="floating-circle circle-3"></div>
          <div className="floating-circle circle-4"></div>
        </div>
        
        {/* Главный логотип/иконка */}
        <div className="telegram-logo-container">
          <div className="telegram-logo">
            <div className="telegram-inner-circle">
              <div className="telegram-plane">
                <svg 
                  width="24" 
                  height="24" 
                  viewBox="0 0 24 24" 
                  fill="none"
                >
                  <path
                    d="M2 12L22 2L18 22L12 18L8 14L2 12Z"
                    stroke="var(--tg-theme-button-text-color, #ffffff)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 18L18 22L22 2L12 18Z"
                    fill="var(--tg-theme-button-text-color, #ffffff)"
                    opacity="0.3"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Прогресс бар */}
        <div className="telegram-progress-container">
          <div className="telegram-progress-bar">
            <div className="telegram-progress-fill"></div>
          </div>
        </div>

        {/* Текст */}
        <div className="telegram-text-container">
          <Paragraph style={titleStyles}>
            {loadingText}
          </Paragraph>
          <Paragraph style={subtitleStyles}>
            Telegram WebApp
          </Paragraph>
        </div>

        {/* Пульсирующие точки */}
        <div className="telegram-dots">
          <div className="dot dot-1"></div>
          <div className="dot dot-2"></div>
          <div className="dot dot-3"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Result
        status="warning"
        title={t('loader_error_title')}
        subTitle={t('loader_error_subtitle')}
        style={{
          background: 'var(--tg-theme-bg-color, #ffffff)',
          color: 'var(--tg-theme-text-color, #000000)',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}
        extra={
          <Alert
            message={t('loader_dev_mode')}
            description={t('loader_dev_mode_desc')}
            type="info"
            showIcon
            style={{
              backgroundColor: 'var(--tg-theme-section-bg-color, #f8f9fa)',
              borderColor: 'var(--tg-theme-button-color, #007aff)',
              color: 'var(--tg-theme-text-color, #000000)'
            }}
          />
        }
      />
    );
  }

  if (!window.Telegram?.WebApp) {
    return (
      <Result
        icon={<MobileOutlined style={{ color: 'var(--tg-theme-button-color, #007aff)' }} />}
        title={t('loader_open_title')}
        subTitle={t('loader_open_subtitle')}
        style={{
          background: 'var(--tg-theme-bg-color, #ffffff)',
          color: 'var(--tg-theme-text-color, #000000)',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}
        extra={
          <Alert
            message={t('loader_dev_title')}
            description={t('loader_dev_desc')}
            type="warning"
            showIcon
            style={{
              backgroundColor: 'var(--tg-theme-section-bg-color, #f8f9fa)',
              borderColor: 'var(--tg-theme-destructive-text-color, #ff3b30)',
              color: 'var(--tg-theme-text-color, #000000)'
            }}
          />
        }
      />
    );
  }

  return null;
});

TelegramLoader.displayName = 'TelegramLoader';

export default TelegramLoader; 